// Start-up ordering helpers for the shell. On a cold first launch the start-up JS, the dictionary
// bundle and the first screen's chunk share one thin connection; background work (warming every
// section chunk, registering the service worker that downloads the whole precache) waits until
// the app is ready and has painted, so it never competes with them.
import { store } from '../state/store';

interface NetInfo {
  saveData?: boolean;
  effectiveType?: string;
}

/** Data saver on, or a 2G/3G connection (navigator.connection, where the browser has it). */
export function slowNetwork(): boolean {
  const c = typeof navigator !== 'undefined' ? (navigator as Navigator & { connection?: NetInfo }).connection : undefined;
  return !!c && (!!c.saveData || c.effectiveType === 'slow-2g' || c.effectiveType === '2g' || c.effectiveType === '3g');
}

let readyPainted: Promise<void> | null = null;

/** Resolves once the app is ready (dictionary and course applied) and that screen has painted. */
export function afterReadyAndPaint(): Promise<void> {
  readyPainted ??= new Promise((resolve) => {
    const painted = () => requestAnimationFrame(() => setTimeout(resolve, 0));
    if (store.get().phase === 'ready') {
      painted();
      return;
    }
    const off = store.subscribe(() => {
      if (store.get().phase !== 'ready') return;
      off();
      painted();
    });
  });
  return readyPainted;
}

/**
 * Resolves true when a service worker controls the page (its precache then serves every chunk),
 * false without one or when none has taken control within `maxMs`.
 */
export function whenControlled(maxMs = 60_000): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return Promise.resolve(false);
  if (navigator.serviceWorker.controller) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(false), maxMs);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.clearTimeout(timer);
        resolve(true);
      },
      { once: true },
    );
  });
}
